/**
 * The host's decisions about a server-computed import plan.
 *
 * Studio no longer works out which guest a pasted row means — the API does, with
 * the same `InviteeImportMatcher` its write runs, and answers with a per-row
 * plan. This module is only bookkeeping over that answer: which proposed matches
 * the host accepted, and what to POST as a result.
 *
 * The rule that shapes everything here: a row carries an invitee id ONLY if the
 * host looked at that guest and said yes. Anything else goes back with no id and
 * inserts as a new guest. A duplicate row is recoverable in one click; a merge of
 * two different people sends one guest's invitation to the other's address and
 * leaves no trace of the guest it overwrote.
 */
import type { GroupInviteeInput } from '../types/groups';

export type InviteeImportAction = 'update' | 'create' | 'ambiguous' | 'conflict';

export type InviteeImportMatchKey =
  | 'invitee_id' | 'external_id' | 'email' | 'phone' | 'name' | 'none';

/** An existing guest, as the server describes them for a confirmation row. */
export interface InviteeImportMatchedGuest {
  id: string;
  fullName: string;
  email?: string | null;
  phone?: string | null;
  rsvpStatus: string;
  invitedBy?: string | null;
  createdAt: string;
}

export interface InviteeImportPlanRow {
  rowIndex: number;
  fullName: string;
  email?: string | null;
  phone?: string | null;
  action: InviteeImportAction;
  matchKey: InviteeImportMatchKey;
  matchedInviteeId?: string | null;
  matchedGuest?: InviteeImportMatchedGuest | null;
  /** The server's verdict: this match is a guess and needs a human. */
  requiresConfirmation: boolean;
  reason: string;
  candidates: InviteeImportMatchedGuest[];
}

export interface InviteeImportPreview {
  rows: InviteeImportPlanRow[];
  updateCount: number;
  createCount: number;
  ambiguousCount: number;
  conflictCount: number;
  requiresConfirmationCount: number;
  rosterBefore: number;
  rosterAfter: number;
  rosterFingerprint: string;
}

export interface InviteeImportRowError {
  row: number;
  fullName: string;
  error: string;
}

export interface InviteeImportResult {
  updatedCount: number;
  createdCount: number;
  skippedCount: number;
  invitees: unknown[];
  errors: InviteeImportRowError[];
}

/**
 * What the host decided about one row.
 *
 * `pending` is its own state rather than a default of `accept`: a row nobody has
 * looked at must not be indistinguishable from one somebody approved.
 */
export type RowDecision =
  | { kind: 'accept'; inviteeId: string }
  | { kind: 'create' }
  | { kind: 'pending' };

export type ImportDecisions = Record<number, RowDecision>;

/**
 * The starting decisions for a plan.
 *
 * Auto-accepts only what the server said is not a guess — an id, an export id or
 * an email address. Name and phone matches, and anything ambiguous, start
 * `pending` and stay out of the write until the host acts on them.
 */
export function defaultDecisions(preview: InviteeImportPreview): ImportDecisions {
  const decisions: ImportDecisions = {};
  preview.rows.forEach((row) => {
    if (row.action === 'update' && row.matchedInviteeId && !row.requiresConfirmation) {
      decisions[row.rowIndex] = { kind: 'accept', inviteeId: row.matchedInviteeId };
    } else if (row.action === 'create') {
      decisions[row.rowIndex] = { kind: 'create' };
    } else {
      decisions[row.rowIndex] = { kind: 'pending' };
    }
  });
  return decisions;
}

/** Rows the host still has to rule on before the import can be trusted. */
export function pendingRows(
  preview: InviteeImportPreview,
  decisions: ImportDecisions,
): InviteeImportPlanRow[] {
  return preview.rows.filter((row) => (decisions[row.rowIndex] ?? { kind: 'pending' }).kind === 'pending');
}

/**
 * The rows a bulk control may touch: proposed matches the SERVER flagged as
 * guesses, and only those.
 *
 * The predicate is `requiresConfirmation`, read off the plan — not a local
 * opinion about which match keys are safe. This filter used to be
 * `matchKey !== 'phone'`, a second copy of the server's safety rule living in
 * the client, which is the exact defect class the whole matcher rewrite exists
 * to kill: the server marks a name match as needing a human, and the client
 * quietly decided one class of them did not. Add a key to the server's
 * confirm-list tomorrow and that filter would have bulk-accepted it.
 *
 * Rows the server did NOT flag are already accepted by `defaultDecisions`, so
 * they never reach a bulk control; ambiguous and conflicting rows carry no
 * `matchedInviteeId` at all and so cannot be accepted by one.
 */
export function confirmableRows(preview: InviteeImportPreview): InviteeImportPlanRow[] {
  return preview.rows.filter(
    (row) => row.action === 'update' && Boolean(row.matchedInviteeId) && row.requiresConfirmation,
  );
}

/**
 * Accept every proposed match the server asked a human to confirm.
 *
 * This is a bulk control over guesses, so it exists only behind an explicit
 * acknowledgement in the panel — never as a default, never as a button a host
 * can hit on the way past. 137 name matches are otherwise 137 clicks, and a
 * host rage-clicking "Yes, same person" 137 times is not safer than one
 * deliberate assertion over a named set. What is NOT acceptable is a control
 * labelled "unambiguous" that silently accepts rows the server said were
 * ambiguous in the only sense that matters: two humans can share a name, and on
 * this roster ("Alex", "Alex Greeley", "Alex Merriman", "Mom", "Dad") a wrong
 * merge sends one guest's confirmation to another guest's address.
 */
export function acceptAllConfirmable(
  preview: InviteeImportPreview,
  decisions: ImportDecisions,
): ImportDecisions {
  const next = { ...decisions };
  confirmableRows(preview).forEach((row) => {
    next[row.rowIndex] = { kind: 'accept', inviteeId: row.matchedInviteeId as string };
  });
  return next;
}

export interface ImportSummary {
  /** Rows that will merge into an existing guest. */
  updates: number;
  /** Rows that will insert. Includes everything the host declined to match. */
  creates: number;
  /** Rows nobody has ruled on yet. */
  pending: number;
  rosterAfter: number;
}

export function summarizeDecisions(
  preview: InviteeImportPreview,
  decisions: ImportDecisions,
): ImportSummary {
  let updates = 0;
  let creates = 0;
  let pending = 0;

  preview.rows.forEach((row) => {
    const decision = decisions[row.rowIndex] ?? { kind: 'pending' };
    if (decision.kind === 'accept') updates += 1;
    else if (decision.kind === 'create') creates += 1;
    else pending += 1;
  });

  // Pending rows are excluded from the projection rather than assumed either
  // way — the host is told to resolve them, not shown a number that quietly
  // guesses on their behalf.
  return { updates, creates, pending, rosterAfter: preview.rosterBefore + creates };
}

/**
 * The rows to POST. `parsed` is the original paste, in the order the preview was
 * computed from, so `rowIndex` indexes into it directly.
 *
 * A pending row is sent with no id: the host never approved a match for it, and
 * inserting is the recoverable answer.
 */
export function buildImportRows(
  parsed: GroupInviteeInput[],
  preview: InviteeImportPreview,
  decisions: ImportDecisions,
): GroupInviteeInput[] {
  return preview.rows.map((row) => {
    const input = parsed[row.rowIndex];
    const decision = decisions[row.rowIndex] ?? { kind: 'pending' };
    if (decision.kind === 'accept') {
      return { ...input, inviteeId: decision.inviteeId };
    }
    // Explicitly strip any id rather than trusting the parsed row not to carry
    // one: a rejected match must not be resurrected by a leftover field.
    const { inviteeId: _dropped, ...rest } = input;
    return rest;
  });
}

/** Human-readable label for why a match was proposed. */
export function matchKeyLabel(key: InviteeImportMatchKey): string {
  switch (key) {
    case 'invitee_id': return 'Tripsby ID';
    case 'external_id': return 'export ID';
    case 'email': return 'email';
    case 'phone': return 'phone';
    case 'name': return 'name only';
    default: return '';
  }
}

/** The line beside the bulk-accept checkbox: the host vouches for a stated number of rows, in grammar that agrees. */
export function bulkVouchSentence(count: number): string {
  return count === 1
    ? 'I have checked the guest shown below and they are the person I meant.'
    : `I have checked the ${count} guests shown below and each is the person I meant.`;
}

/** The bulk-accept button: "Accept this match" / "Accept these 3 matches". */
export function bulkAcceptLabel(count: number): string {
  return count === 1 ? 'Accept this match' : `Accept these ${count} matches`;
}
